import type { FilteredDashboardApiEndpointKeys, dashboardApiEndpoints } from "@dashboardApi/apis";
import type { MutationFetcherFunctionWithShadowError, EndpointMethod } from "@dashboardApi/apis/types";

import type { FetchRequestError } from "@lib/errors";
import { postMessageFetcher } from "@lib/postMessageFetcher";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";

/**
 * This hook returns a function that can be used as a fetcher function for react-query.
 * The fetcher function utilizes the PostMessage and MessageChannel API to communicate with the parent window.
 * This is used by the message composer in the editor domain to communicate with the parent window.
 * The editor domain is not allowed to communicate with the backend directly to prevent XSS attacks.
 * @param endpoint a key into the global API endpoint definitions.
 * @param method the HTTP method to use for the request.
 */
export const useEditorDomainApiMutationFetcher = <
  Arguments,
  Result,
  Error extends FetchRequestError,
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, Method>,
  Method extends Exclude<EndpointMethod, "GET" | "POSTWS">,
>(
  endpoint: Endpoint,
  method: Method
): MutationFetcherFunctionWithShadowError<Error, Arguments, Result> => {
  const dashboardUrl = useBrazeDashboardConfig("dashboardUrl");

  const result: MutationFetcherFunctionWithShadowError<Error, Arguments, Result> = (args: Arguments) => {
    return postMessageFetcher({
      endpoint,
      method,
      args,
      targetOrigin: dashboardUrl,
    });
  };
  return result;
};
